import type { AxiosInstance } from 'axios';
import authService from './auth.service';
import type { Role } from '../lib/roleRouting';
import type {
  AnnouncedEntity,
  ConcentrationAccount,
  ConcentrationAccountCreateBody,
  ConcentrationAccountPatchBody,
  ReceivingEntity,
} from '../types/finance';

// Alias kept for callers that already say `UserRole`. New code should
// import `Role` from `lib/roleRouting` directly.
export type UserRole = Role;

export interface AdminUserSummary {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  timezone: string | null;
  role: UserRole;
  is_active: boolean;
  must_change_password: boolean;
  totp_enabled: boolean;
  /** A seeded account nobody signs in with: it is never offered "Activate". */
  is_system_account?: boolean;
  failed_attempts: number;
  lockout_until: string | null;
  last_login_at: string | null;
  auth_method: string;
  assigned_agent_id: string | null;
  /** Reporting manager (only meaningful when role='agent'). Drives the
   *  rejection-routing fallback in workflow.propose_reject: NULL → fan
   *  out to all active managers; set → notify just this one. */
  manager_id: string | null;
  manager_name: string | null;
  /** Display name of the assigned agent (only set when assigned_agent_id
   *  is not null — i.e. clients with an agent linked through
   *  client_profiles). Resolved server-side by a batched lookup. */
  assigned_agent_name: string | null;
  created_at: string;
}

export interface AdminUserListResponse {
  items: AdminUserSummary[];
  total: number;
}

export interface AdminActionResponse {
  user_id: string;
  action: string;
  correlation_id: string;
  affected_records: number;
  message: string;
}

export interface ListUsersParams {
  search?: string;
  role?: UserRole;
  /** Drop any user whose role matches. Used by /admin/staff to exclude
   *  clients in a single backend call without paginating clients into
   *  the staff view. */
  exclude_role?: UserRole;
  is_active?: boolean;
  limit?: number;
  offset?: number;
}

/** Request body for creating an AGENT account (POST /admin/users). A manager or a
 *  super admin is proposed by one super admin and confirmed by another
 *  (`proposeStaffChange`); clients come through /invitations. Optional fields are
 *  omitted, not sent as null, when empty. `mfa_code` is the acting
 *  super_admin's fresh authenticator code, always required. */
export interface CreateStaffBody {
  email: string;
  first_name: string;
  last_name: string;
  role: Extract<UserRole, 'agent'>;
  timezone?: string;
  manager_id?: string;
  reason: string;
  mfa_code: string;
}

export type StaffChangeKind = 'create' | 'deactivate' | 'reactivate' | 'change_role';
export type StaffRole = Extract<UserRole, 'agent' | 'manager' | 'super_admin'>;

/** A change that gives or takes away the manager or the super admin role: proposed by one
 *  super admin, confirmed by a different one who is not the person it names (#326). */
export interface StaffChange {
  id: string;
  kind: StaffChangeKind;
  target_user_id: string | null;
  target_name: string | null;
  target_email: string | null;
  /** The new person's address and names, for a creation only. */
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  timezone: string | null;
  /** The role given (create, change_role) or held by the account (deactivate, reactivate). */
  role: StaffRole;
  previous_role: StaffRole | null;
  reason: string;
  proposed_by: string;
  proposed_by_name: string;
  proposed_at: string;
  status: 'proposed' | 'confirmed' | 'cancelled';
  decided_by: string | null;
  decided_at: string | null;
  decision_note: string | null;
  created_user_id: string | null;
  /** The reader proposed it: a different super admin confirms it. */
  you_proposed: boolean;
  /** It is about the reader's own account: they never decide on it. */
  about_you: boolean;
}

/** What a super admin proposes, with their authenticator code. */
export interface ProposeStaffChangeBody {
  kind: StaffChangeKind;
  target_user_id?: string;
  new_role?: StaffRole;
  email?: string;
  first_name?: string;
  last_name?: string;
  timezone?: string;
  reason: string;
  mfa_code: string;
}

class AdminService {
  private get api(): AxiosInstance {
    return authService.getApi();
  }

  async listUsers(params: ListUsersParams = {}): Promise<AdminUserListResponse> {
    const res = await this.api.get<AdminUserListResponse>('/api/v1/admin/users', { params });
    return res.data;
  }

  /** Provision a new internal staff account (agent | manager). super_admin
   *  only. The new user authenticates via Google SSO — no password is set;
   *  they sign in with the matching Google Workspace email. */
  async createStaff(body: CreateStaffBody): Promise<AdminActionResponse> {
    const res = await this.api.post<AdminActionResponse>('/api/v1/admin/users', body);
    return res.data;
  }

  async deactivateUser(
    userId: string,
    body: { reason: string; reassign_clients_to?: string | null },
  ): Promise<AdminActionResponse> {
    const res = await this.api.patch<AdminActionResponse>(
      `/api/v1/admin/users/${userId}/deactivate`,
      body,
    );
    return res.data;
  }

  /** The account actions that take nothing but the reason kept in the audit log. */
  private async patchWithReason(
    userId: string,
    action: string,
    reason: string,
  ): Promise<AdminActionResponse> {
    const res = await this.api.patch<AdminActionResponse>(
      `/api/v1/admin/users/${userId}/${action}`,
      { reason },
    );
    return res.data;
  }

  async activateUser(userId: string, reason: string): Promise<AdminActionResponse> {
    return this.patchWithReason(userId, 'activate', reason);
  }

  async forcePasswordChange(userId: string, reason: string): Promise<AdminActionResponse> {
    return this.patchWithReason(userId, 'force-password-change', reason);
  }

  async unlockAccount(userId: string, reason: string): Promise<AdminActionResponse> {
    return this.patchWithReason(userId, 'unlock', reason);
  }

  async revokeSessions(userId: string, reason: string): Promise<AdminActionResponse> {
    const res = await this.api.post<AdminActionResponse>(
      `/api/v1/admin/users/${userId}/revoke-sessions`,
      { reason },
    );
    return res.data;
  }

  async changeRole(
    userId: string,
    body: { new_role: UserRole; reason: string; mfa_code: string },
  ): Promise<AdminActionResponse> {
    const res = await this.api.patch<AdminActionResponse>(
      `/api/v1/admin/users/${userId}/role`,
      body,
    );
    return res.data;
  }

  /** The changes that wait for a second super admin. */
  async listStaffChanges(): Promise<StaffChange[]> {
    const res = await this.api.get<{ items: StaffChange[] }>('/api/v1/admin/staff-changes');
    return res.data.items;
  }

  async proposeStaffChange(body: ProposeStaffChangeBody): Promise<StaffChange> {
    const res = await this.api.post<StaffChange>('/api/v1/admin/staff-changes', body);
    return res.data;
  }

  /** A super admin who did not propose the change, and is not the person it names, confirms it
   *  with their code; it takes effect at once. */
  async confirmStaffChange(id: string, mfaCode: string): Promise<StaffChange> {
    const res = await this.api.post<{ change: StaffChange; user_id: string }>(
      `/api/v1/admin/staff-changes/${id}/confirm`,
      { mfa_code: mfaCode },
    );
    return res.data.change;
  }

  async cancelStaffChange(id: string, note: string): Promise<StaffChange> {
    const res = await this.api.post<StaffChange>(`/api/v1/admin/staff-changes/${id}/cancel`, {
      note,
    });
    return res.data;
  }

  async reassignClients(
    fromAgentId: string,
    body: { target_agent_id: string; reason: string },
  ): Promise<AdminActionResponse> {
    const res = await this.api.post<AdminActionResponse>(
      `/api/v1/admin/users/${fromAgentId}/reassign-clients`,
      body,
    );
    return res.data;
  }

  async resetTOTP(
    userId: string,
    body: { reason: string; mfa_code: string },
  ): Promise<AdminActionResponse> {
    const res = await this.api.post<AdminActionResponse>(
      `/api/v1/admin/users/${userId}/totp-reset`,
      body,
    );
    return res.data;
  }

  /** Assign (or clear) an agent's reporting manager. The backend
   *  validates target is role='agent' and manager_id (if not null)
   *  points at an active role='manager' row. Used by /admin/users to
   *  close the previously-orphan PATCH /admin/users/{id}/manager
   *  endpoint and stop rejection emails from fanning out to all
   *  managers by default. */
  async setManager(
    userId: string,
    managerId: string | null,
    reason?: string,
  ): Promise<AdminActionResponse> {
    const res = await this.api.patch<AdminActionResponse>(
      `/api/v1/admin/users/${userId}/manager`,
      { manager_id: managerId, reason: reason ?? null },
    );
    return res.data;
  }

  /** Manager / super_admin updates another user's IANA timezone.
   *  Agents are forbidden from self-serving this — the scheduling
   *  layer trusts the column when it renders slots. */
  async changeTimezone(
    userId: string,
    body: { timezone: string; reason: string },
  ): Promise<AdminActionResponse> {
    const res = await this.api.patch<AdminActionResponse>(
      `/api/v1/admin/users/${userId}/timezone`,
      body,
    );
    return res.data;
  }

  async getConcentrationAccounts(): Promise<ConcentrationAccount[]> {
    const res = await this.api.get<ConcentrationAccount[]>(
      '/api/v1/admin/concentration-accounts',
    );
    return res.data;
  }

  /** The fund series funds reports as receiving each offered product's capital,
   *  with whether each has its bank account registered. */
  async getReceivingEntities(): Promise<ReceivingEntity[]> {
    const res = await this.api.get<ReceivingEntity[]>(
      '/api/v1/admin/concentration-accounts/receiving-entities',
    );
    return res.data;
  }

  /** The companies books announced as ready that have no bank account in force or waiting
   *  (super_admin only): where the account of a fund that is not receiving yet is picked. */
  async getAnnouncedEntities(): Promise<AnnouncedEntity[]> {
    const res = await this.api.get<AnnouncedEntity[]>(
      '/api/v1/admin/concentration-accounts/announced-entities',
    );
    return res.data;
  }

  async createConcentrationAccount(
    data: ConcentrationAccountCreateBody,
  ): Promise<ConcentrationAccount> {
    const res = await this.api.post<ConcentrationAccount>(
      '/api/v1/admin/concentration-accounts',
      data,
    );
    return res.data;
  }

  /** A super admin who did not enter the account confirms it with their current
   *  authenticator code. It comes into force and replaces the fund's previous one. */
  async confirmConcentrationAccount(id: string, mfaCode: string): Promise<ConcentrationAccount> {
    const res = await this.api.post<ConcentrationAccount>(
      `/api/v1/admin/concentration-accounts/${id}/confirm`,
      { mfa_code: mfaCode },
    );
    return res.data;
  }

  async updateConcentrationAccount(
    id: string,
    patch: ConcentrationAccountPatchBody,
  ): Promise<ConcentrationAccount> {
    const res = await this.api.patch<ConcentrationAccount>(
      `/api/v1/admin/concentration-accounts/${id}`,
      patch,
    );
    return res.data;
  }
}

export default new AdminService();
